/* eslint no-console: 0 */
import * as types from '../mutation-types';
import Report from '../../api/reports';
import { downloadCsvFile, generateFileName } from '../../helper/downloadHelper';
import AnalyticsHelper from '../../helper/AnalyticsHelper';
import { REPORTS_EVENTS } from '../../helper/AnalyticsHelper/events';
import {
  reconcileHeatmapData,
  clampDataBetweenTimeline,
} from 'shared/helpers/ReportsDataHelper';
import JsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const state = {
  fetchingStatus: false,
  accountReport: {
    isFetching: {
      conversations_count: false,
      incoming_messages_count: false,
      outgoing_messages_count: false,
      avg_first_response_time: false,
      avg_resolution_time: false,
      resolutions_count: false,
      bot_resolutions_count: false,
      bot_handoffs_count: false,
      reply_time: false,
    },
    data: {
      conversations_count: [],
      incoming_messages_count: [],
      outgoing_messages_count: [],
      avg_first_response_time: [],
      avg_resolution_time: [],
      resolutions_count: [],
      bot_resolutions_count: [],
      bot_handoffs_count: [],
      reply_time: [],
    },
  },
  accountSummary: {
    avg_first_response_time: 0,
    avg_resolution_time: 0,
    conversations_count: 0,
    incoming_messages_count: 0,
    outgoing_messages_count: 0,
    reply_time: 0,
    resolutions_count: 0,
    bot_resolutions_count: 0,
    bot_handoffs_count: 0,
    previous: {},
  },
  botSummary: {
    bot_resolutions_count: 0,
    bot_handoffs_count: 0,
    previous: {},
  },
  overview: {
    uiFlags: {
      isFetchingAccountConversationMetric: false,
      isFetchingAccountConversationsHeatmap: false,
      isFetchingAgentConversationMetric: false,
      isFetchingConversationsList: false,
    },
    accountConversationMetric: {},
    accountConversationHeatmap: [],
    agentConversationMetric: [],
  },
  conversationsList: {
    conversations: [],
    currentPage: 1,
  },
  reportRef: null,
};

const getters = {
  getAccountReports(_state) {
    return _state.accountReport;
  },
  getAccountSummary(_state) {
    return _state.accountSummary;
  },
  getBotSummary(_state) {
    return _state.botSummary;
  },
  getAccountConversationMetric(_state) {
    return _state.overview.accountConversationMetric;
  },
  getAccountConversationHeatmapData(_state) {
    return _state.overview.accountConversationHeatmap;
  },
  getAgentConversationMetric(_state) {
    return _state.overview.agentConversationMetric;
  },
  getOverviewUIFlags($state) {
    return $state.overview.uiFlags;
  },
  getConversationsList(_state) {
    return _state.conversationsList.conversations;
  },
  getConversationsListCurrentPage(_state) {
    return _state.conversationsList.currentPage;
  },
};

export const actions = {
  fetchAccountReport({ commit }, reportObj) {
    const { metric } = reportObj;
    commit(types.default.TOGGLE_ACCOUNT_REPORT_LOADING, {
      metric,
      value: true,
    });
    Report.getReports(reportObj).then(accountReport => {
      let { data } = accountReport;
      data = clampDataBetweenTimeline(data, reportObj.from, reportObj.to);
      commit(types.default.SET_ACCOUNT_REPORTS, {
        metric,
        data,
      });
      commit(types.default.TOGGLE_ACCOUNT_REPORT_LOADING, {
        metric,
        value: false,
      });
    });
  },
  fetchConversationsList({ commit }, reportObj) {
    commit(types.default.TOGGLE_CONVERSATIONS_LIST_LOADING, true);
    Report.getConversationsList({
      metric: reportObj.metric || 'conversations_count',
      from: reportObj.from,
      to: reportObj.to,
      type: reportObj.type,
      id: reportObj.id,
      page: reportObj.page || 1,
      groupBy: reportObj.groupBy,
      businessHours: reportObj.businessHours,
    })
      .then(response => {
        commit(types.default.SET_CONVERSATIONS_LIST, {
          conversations: response.data,
          page: reportObj.page || 1,
        });
        commit(types.default.TOGGLE_CONVERSATIONS_LIST_LOADING, false);
      })
      .catch(error => {
        console.error(error);
        commit(types.default.TOGGLE_CONVERSATIONS_LIST_LOADING, false);
      });
  },
  fetchAccountConversationHeatmap({ commit }, reportObj) {
    commit(types.default.TOGGLE_HEATMAP_LOADING, true);
    Report.getReports({ ...reportObj, groupBy: 'hour' }).then(heatmapData => {
      let { data } = heatmapData;
      data = clampDataBetweenTimeline(data, reportObj.from, reportObj.to);

      data = reconcileHeatmapData(
        data,
        state.overview.accountConversationHeatmap
      );

      commit(types.default.SET_HEATMAP_DATA, data);
      commit(types.default.TOGGLE_HEATMAP_LOADING, false);
    });
  },
  fetchAccountSummary({ commit }, reportObj) {
    Report.getSummary(
      reportObj.from,
      reportObj.to,
      reportObj.type,
      reportObj.id,
      reportObj.groupBy,
      reportObj.businessHours
    )
      .then(accountSummary => {
        commit(types.default.SET_ACCOUNT_SUMMARY, accountSummary.data);
      })
      .catch(() => {
        commit(types.default.TOGGLE_ACCOUNT_REPORT_LOADING, false);
      });
  },
  fetchBotSummary({ commit }, reportObj) {
    Report.getBotSummary({
      from: reportObj.from,
      to: reportObj.to,
      groupBy: reportObj.groupBy,
      businessHours: reportObj.businessHours,
    })
      .then(botSummary => {
        commit(types.default.SET_BOT_SUMMARY, botSummary.data);
      })
      .catch(() => {
        commit(types.default.TOGGLE_ACCOUNT_REPORT_LOADING, false);
      });
  },
  fetchAccountConversationMetric({ commit }, reportObj) {
    commit(types.default.TOGGLE_ACCOUNT_CONVERSATION_METRIC_LOADING, true);
    Report.getConversationMetric(reportObj.type)
      .then(accountConversationMetric => {
        commit(
          types.default.SET_ACCOUNT_CONVERSATION_METRIC,
          accountConversationMetric.data
        );
        commit(types.default.TOGGLE_ACCOUNT_CONVERSATION_METRIC_LOADING, false);
      })
      .catch(() => {
        commit(types.default.TOGGLE_ACCOUNT_CONVERSATION_METRIC_LOADING, false);
      });
  },
  fetchAgentConversationMetric({ commit }, reportObj) {
    commit(types.default.TOGGLE_AGENT_CONVERSATION_METRIC_LOADING, true);
    Report.getConversationMetric(
      reportObj.type,
      reportObj.page,
      reportObj.agentId
    )
      .then(agentConversationMetric => {
        commit(
          types.default.SET_AGENT_CONVERSATION_METRIC,
          agentConversationMetric.data
        );
        commit(types.default.TOGGLE_AGENT_CONVERSATION_METRIC_LOADING, false);
      })
      .catch(() => {
        commit(types.default.TOGGLE_AGENT_CONVERSATION_METRIC_LOADING, false);
      });
  },
  downloadAgentReports(_, reportObj) {
    return Report.getAgentReports(reportObj)
      .then(response => {
        downloadCsvFile(reportObj.fileName, response.data);
        AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
          reportType: 'agent',
          businessHours: reportObj?.businessHours,
        });
      })
      .catch(error => {
        console.error(error);
      });
  },
  downloadLabelReports(_, reportObj) {
    return Report.getLabelReports(reportObj)
      .then(response => {
        downloadCsvFile(reportObj.fileName, response.data);
        AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
          reportType: 'label',
          businessHours: reportObj?.businessHours,
        });
      })
      .catch(error => {
        console.error(error);
      });
  },
  downloadInboxReports(_, reportObj) {
    return Report.getInboxReports(reportObj)
      .then(response => {
        downloadCsvFile(reportObj.fileName, response.data);
        AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
          reportType: 'inbox',
          businessHours: reportObj?.businessHours,
        });
      })
      .catch(error => {
        console.error(error);
      });
  },
  downloadTeamReports(_, reportObj) {
    return Report.getTeamReports(reportObj)
      .then(response => {
        downloadCsvFile(reportObj.fileName, response.data);
        AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
          reportType: 'team',
          businessHours: reportObj?.businessHours,
        });
      })
      .catch(error => {
        console.error(error);
      });
  },
  downloadAccountConversationHeatmap(_, reportObj) {
    Report.getConversationTrafficCSV()
      .then(response => {
        downloadCsvFile(
          generateFileName({
            type: 'Conversation traffic',
            to: reportObj.to,
          }),
          response.data
        );

        AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
          reportType: 'conversation_heatmap',
          businessHours: false,
        });
      })
      .catch(error => {
        console.error(error);
      });
  },
  setReportRef({ commit }, ref) {
    commit(types.default.SET_REPORT_REF, ref);
  },
  async generatePDF(_, reportObj) {
    if (!state.reportRef) {
      console.error('Report content reference not found.');
      return;
    }

    try {
      const original = state.reportRef;
      const clone = document.createElement('div');
      clone.insertBefore(original.cloneNode(true), null);
      clone.style.width = '100%';
      clone.style.height = 'auto';
      clone.style.padding = '0 20px';

      const header = document.createElement('div');
      header.innerHTML = `
        <div style="margin: 20px 0; padding: 30px 10px 0 25px; display: flex; justify-content: space-between;">
          <div style="color: #343333; font-size: 18px;">
            <h2 style="font-size: 40px; margin-bottom: 0px; color: #343333">Indicadores ${reportObj.title}</h2><br>
            <b>${reportObj.type}: </b> ${reportObj.selectedFilter.name || reportObj.selectedFilter.title}<br>
            <b>Gerado em: </b> ${new Date().toLocaleDateString('pt-BR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      })}<br>
            <b>Duração: </b> ${new Date(
        reportObj.from * 1000
      ).toLocaleDateString('pt-BR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      })} - ${new Date(reportObj.to * 1000).toLocaleDateString('pt-BR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      })}<br>
            <b>Agrupado por: </b> ${reportObj.groupBy.period}<br>
          </div>
          <div style="margin-top: 20px; text-align: right;">
            <img style="width: 200px" src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAI0AAAAwCAYAAAAsCvkOAAAACXBIWXMAAA7EAAAOxAGVKw4bAAAEgmlUWHRYTUw6Y29tLmFkb2JlLnhtcAAAAAAAPD94cGFja2V0IGJlZ2luPSfvu78nIGlkPSdXNU0wTXBDZWhpSHpyZVN6TlRjemtjOWQnPz4KPHg6eG1wbWV0YSB4bWxuczp4PSdhZG9iZTpuczptZXRhLyc+CjxyZGY6UkRGIHhtbG5zOnJkZj0naHR0cDovL3d3dy53My5vcmcvMTk5OS8wMi8yMi1yZGYtc3ludGF4LW5zIyc+CgogPHJkZjpEZXNjcmlwdGlvbiByZGY6YWJvdXQ9JycKICB4bWxuczpBdHRyaWI9J2h0dHA6Ly9ucy5hdHRyaWJ1dGlvbi5jb20vYWRzLzEuMC8nPgogIDxBdHRyaWI6QWRzPgogICA8cmRmOlNlcT4KICAgIDxyZGY6bGkgcmRmOnBhcnNlVHlwZT0nUmVzb3VyY2UnPgogICAgIDxBdHRyaWI6Q3JlYXRlZD4yMDI0LTExLTE5PC9BdHRyaWI6Q3JlYXRlZD4KICAgICA8QXR0cmliOkV4dElkPjE8L0F0dHJpYjpFeHRJZD4KICAgICA8QXR0cmliOkZiSWQ+NTI1MjY1OTE0MTc5NTgwPC9BdHRyaWI6RmJJZD4KICAgICA8QXR0cmliOlRvdWNoVHlwZT4yPC9BdHRyaWI6VG91Y2hUeXBlPgogICAgPC9yZGY6bGk+CiAgIDwvcmRmOlNlcT4KICA8L0F0dHJpYjpBZHM+CiA8L3JkZjpEZXNjcmlwdGlvbj4KCiA8cmRmOkRlc2NyaXB0aW9uIHJkZjphYm91dD0nJwogIHhtbG5zOmRjPSdodHRwOi8vcHVybC5vcmcvZGMvZWxlbWVudHMvMS4xLyc+CiAgPGRjOnRpdGxlPgogICA8cmRmOkFsdD4KICAgIDxyZGY6bGkgeG1sOmxhbmc9J3gtZGVmYXVsdCc+RGVzaWduIHNlbSBub21lIC0gMTwvcmRmOmxpPgogICA8L3JkZjpBbHQ+CiAgPC9kYzp0aXRsZT4KIDwvcmRmOkRlc2NyaXB0aW9uPgoKIDxyZGY6RGVzY3JpcHRpb24gcmRmOmFib3V0PScnCiAgeG1sbnM6cGRmPSdodHRwOi8vbnMuYWRvYmUuY29tL3BkZi8xLjMvJz4KICA8cGRmOkF1dGhvcj5BdGVuZGltZW50byBXZWxsb248L3BkZjpBdXRob3I+CiA8L3JkZjpEZXNjcmlwdGlvbj4KCiA8cmRmOkRlc2NyaXB0aW9uIHJkZjphYm91dD0nJwogIHhtbG5zOnhtcD0naHR0cDovL25zLmFkb2JlLmNvbS94YXAvMS4wLyc+CiAgPHhtcDpDcmVhdG9yVG9vbD5DYW52YSAoUmVuZGVyZXIpIGRvYz1EQUdXN0xxY29VNCB1c2VyPVVBRXl1OHlNS2d3PC94bXA6Q3JlYXRvclRvb2w+CiA8L3JkZjpEZXNjcmlwdGlvbj4KPC9yZGY6UkRGPgo8L3g6eG1wbWV0YT4KPD94cGFja2V0IGVuZD0ncic/PoKq01oAACO1SURBVHic7Xt3fFzVlf/3vjJ9RiNpRtWSRnKRLRe5Ydwl27hhY0iCgcBCCD9SKMsCgSS7hNiGH7shm8JCCIFAgISywQYDNqa5SG64gqvcJKtabWY0I42mvvfu2T9mRhq5heIfZj8/f+1nS/NuOefcc0+7dxgAbLoWqJg90tImDLoiK9pRIrQojaw+vL76rpPds2bhEi5hAMRNi4CZlSNK0NX+ttlTc7fQ274QzHMNz1UXFs+pXI+0Ol919cUm8xK+SWD8pSEGaup+iYXc1wIQki8IjJMhc6NIIxewFdX8ItJ4Cd8wCBQoclCkew4YWOIzFn9IYFHfdDjrCpct6Ht3CZcAgQ0eqWOAEQQA/coR1xwuQmYW6C4WeZfwTYREW//eRZL+OMWUcgFEKe+IJEMno7R6DG6mc45wCZgxYxa8Xgfr7Mwij+fpc7SqwIgRWcxkCpPXewgNDQ1fJ4kXFOKKhWKU67I7EQ0sEMD1yReciWF1xJL7JXvVnln3XUwSv7lwuVyw2TZg+CCDqSRvvMuZPpTlnHSEXTChEY197ZZhGeoHDZVnjp1cVFxQYjpy7IbeseNL0dhYdfGI/woQMT2KyqLwCYoV7gbjFoj6Hugs+1A4/d9kBN6GW8Tyq/Kw/P7RqLTUwzUfqN58scn+JqACK658khUMPnyZu6b71d42/73o6bp58jXlkeOf/ebTFqwAEFeY65fcnndUOfxyoMX3b/7m4PdHFASz5y3K/aSueb7i9a69yHx8cTAAqABQ9QyYptwiVx/eZJx11TVa1ebtRTOH2saxjkMlXCMI2cMb4W/5jOU5atjEPWrltUB1zUWm/qLiN1gynWUK3uiGSFgZhYQsmcB6p0zJW/zfr/5uSw1q8PDDD7Mja00rg12xa4hBAIEgQM3INd+3vWnwMw0tTxDwv6umIQFxkqt2DqGKYqbNGpExmXb/9e6ZUnAC6rgTRGAMQMN2Rkz08GjvViZf+dimEdv3z6rx8/9d7F44fL8gg+nMsWGNp4KjEC9VMADEObd4Q+Li65ZftwUAxFiWLRL0zQdLtGEACFKoR1k8cYT3RVGfEa6ru4iMfAlIALDpbmD6gvEmpWbbz8SOxn8BVy0QZR/Tm6pI1B9l4MQ1tZTFQuNZyHs1HdowTZuQf++mIcqqWX8JatXui83G14/eyRY4gj49MKAcwUAA55qFiBgAxMJcBnFdSibBGMDDgZhRc7cIms/39RJ+ASAtuwWoGJct832bHxR73T8DYwrZcp8VjYOfxSTzCWH6B+GPp5YDCzIMc7I6RqC74xcI+5egt/lPPGuot+pu/4bKja30/2PVmNhZ6lcMYESMMQZKWuk+AzOgGYQogNjXQuoFhbB8PsC9aRUIeh4AmIKyq++T9JZ7q2nrgRWv7QkzAPO278djf9kU2bw/cx8atFvJ6ngNmmpGd8vjZMtMX15w+ReatKKiAsuWLft/1h4AKhJ/Pm/bhViIMSXfwhTLPNzxnTs+Vz92npJnXGFSGjAM/P0Lwok7McUyD1Os87Bg0gJUVHw+3lLxj2R4G27DyOHXYoY8Gw/c+cA520laV66RQu57GGl6pOU+J7Z99mLl011adSMB8PQ1rG4AZr20hZZ9F4GfZ135Eym8cTKL9Y7RtOjC6oO6128c9SQpjqNUVVUFN84eIZe7yvHoDx6Fx+djoxaVi90fdmtP7EgjJDKN0+F0lsFuX41bbgqyefMGS9gwQz209W16E384J0PXXXcXRh3PY7OnzBa62300XhzPX1n1CtwY6ENdLhfu4HfgRFYam5uZLxyoazYVlJp1otmvTh1TGK7fNFwJj+6impqVcLu/XMRP9FXKWxUYhgDKZ9zKYgqHEeMNmaE9ehJlFEwwRbPd2ZGsaXfhwLb/oGOw4XzB9AysgG/USHbHHZUCqkGHHW6+atUfE7NUwDndCWHHtZg6wmxiQ7uNJq1dLc7KCk7DB1ozdlMT1g8Yn6nPzh6Mxq17GEhDTva0R95rOb7iQ5yV22VzgOX/OpXxw75cCoceQLRX2hme2/7HqsszBB2O6xTbNovLfnzbiaOKx/MRGhr6J3K5XHh88e+EGjU05eD21u8zAZmSTqgumHTdC7/5Q10AGHic7nSWYd6sXwpOITSu6bD7dlEWCrnKW216+wsv7n5tx9mE5IQTt3/nL6YWd93NIX9sHsB77dnmlz79tHVzi/d5Lak4Dxf/BSHzSVEcnj+krV2ZG/CHpmsxtYgxwcA5V3UGqVPSy3syi9LXWfKd+/dvWheNZtRQdYoPXrr0DeYI+yrrD3esRzx+YQBAICodk/+n9HEtdzHGwHscmZ+u8Z/SVE0GGCMiMEAjwhbeTYuP+6uCDagawEde3q8xWdDEweMzCzu6wzN6/ZHpXOVDlahmBWOQZLFXEFGblmnclpVl3fLGdnd9piZqe9vOtA7Dh/8E451FZknG94Lu8FwA3ZZBac+//P6Pt9rtLvxp9hpx/ak143xR+XolqkwiDhvXNFVnlJttaYaPQOZ3pZHZbe+u/jklN49EIfdQgStWyJa9XLacqIqcqTAVTmD5tWAVI0dn8j2td1Kw83qA6XvC9km79gWNPndwN9e0bFEOdhqjXe+OzCp5fO7kh+rv+7u7b6KysqWoam0a23Ko91UlphUAAGNY2LD99YI7Zqb9fNWRMiV1R4fDv0O60jq69nDrajWm5RMIDAwR2b3wxgU3X5XedN1nT9fcNYDOufNuZW7/qXvczd2PgCACQCSsXDlp0qCFFYab9j6x+gmaNesupp+U7jy+Pv2e4H7vLZqi5VEymGUaQICqqEAguiDgC91jrHWvK7AW/WrMt4cfbGxs/EKV3C9qaVwuF273Po7mqZ5CX0PojiNHO6/TFF5IRELcGcYH1BSVAZgR7o3e6jkVaLksW7+qoMj0x+s+3HjyGedtfTTacBuG2SZJjLc81Hky+ACB4jKpjS36p/n/sTjoNx1c5938E69Xu0dTlQzEzxwJYAgHIuMjgegS2Rj8YXaz+sDsiXdW7/zkz1qDfz8ExmMOAER6i0fqOHpGQFtRBlRdC1YxfE4Jdda/A1/jL5kaGs6UULEtS54SjPrbzFb9tjgzWlZvV+j2roaTb+3srJ/4raXLmMvlAgDEPouynlZaklAYBgZGBDncrdwgFeqHWK35fQ7fZrsPk8f5pM6mrjvVqJbPACbEIwimKVoOfD33RuyDBgQI/8d2H9KkiZb2Zu+dCYWJt4/xDE9b71VaQKNi+3UsVxo8Yu97R1Z2e3t/pipaPvWnywKICYmpGABGGreF/JEbOjzqm56D6VfcftPtnztO+vyIK5bL5cJDDz3E6qbVz2w75H874Ak9qEZVF4FEFicoLoZ+ehkAQVW0goA3dO+Jw753Aze0zr3xxj8wp7MMAKDHeLgy2tO7O8O3AySyuOYxTeEZOi3tDqtMyzobPcs0lScUBkjOwcAYMQixsFredtL7Uq7ROiasvAgAEJig8wFgiAXtyHSx0+OrqrcBqpifyT27V7FY7+VMICE+OYFFw0snObxijtX3JmNMQYIoJaKNajjg+XPP0VaX03kNAECLesE55ywpJUosKteyoh5t6uLFU/u2JZEDdtmdEQ7GKhDvwKhfUKy7KzS3Ky1qtVr7zzdWsRpYs2iqqmhZiRA1YT0IGidWVnodWzpvcWHwVOTlcK8yHYgrVkpsSgARA1GKrWVgYGpMG/xpVdOfa99xTupxjvmSynF+qOpTaNljmdtZq74Si6jlAOLKS2AEYgSihKVJPiwpQzAISlQd8VlV/cusK7SI6L8AAM/jLmQW5KRpGk9HivwAME+L75rOUz33ARDYwHcJzvtkyNSolt9Q2/bwrMoNpokTKyEgvfgEMTEENTyE0rSCyqz+jsuWAlSVJlD9oUcEpWc04hVNxBkBKBq4YtAwfTpRe5UoCe6E5BkDCUpEHc392gPTXZWyE2VwjInR8NHZn0AQon2Kw8BAENytnnn7dgdEJHbx5BHDmEOnG6XF1JJUHhIrzDRFyyxJD00twCd9tP7o2tnwNnkXgkhOTW4ZYzzDad3x6od7dPW1Hf8eDSnjUgVEcb8Xk2SxngH7BFk4wRgLAaDk1gPANFUr9HHPbydXFtqnjZt2fg0Qzv96IBgqcm5gU/K3jNy7se5pTdXy0b9JEiIFB2OKIDC/ILKeuB8FT7wDiydqTNN49v4Nx56aVfLxmEUli9gSAIIk9LGSIkPEIqoNRFJcBPEn4Z8JABElFjnBfyyoXpGuTyttbX0CAnXvaCWdaQ/jmp0Hi+ct/1G/xJeXANBGFiHQ+T0goTDJcjnAmBrOHZbRM3MCP9ppshk2JZigpCkJ9US+7TvRUVJZWYnm9ma4PQ37ZFmsTUxAcaIIalibmGeckluSXwiXy4XxWYdYV0dgAeeUdDMDQCDJ3x5aPHLq7QDipr3QMVL2tPUsSFloACBJJ50MyY27rrjCeUXAF16SWFKWUFwSRKE9p8hxv7WoYHoTb7o8d4IwZcTUgu/Jeul4ks5EexYL80mKV3f9+JzxF+x+kdNZBl3pEIlHnCtURStJ0B63MQAxgUWMVsPaMZcVf79ZkRc1ReVFRcMcd+jN8hbGEEvUfxLxNZiqaIVaJPPRIfPnSnXpXRCIzibCPvnIBmlferbtMdkg/iTNYXxBlEQ3BqxjHFzj5sCp3injhNeYIOryg9DZ1hBIRMfBa1GfLVRUABUVAEpc4KHuBeCKAXHJnVZ6ICDa/e2SiQZpUGZkNRiiyUVjjDFV07Ki+uA8Q0Eh23F0B6Kt6R6DSfdxsj8lWqqalpvj7B33p1P3IT0wBh3tOWmRiDIgnSKiZGDJAAZPq2/BpOE2uaxsKWwBG9tbtX10LBJzpe5QImIGi+H9YSMKuvdvbf4xCKYUK0SMsfDQMbn3ZAQ8z+w4vLbtcNPHyvYDb3cZiuS3YKMfiJLQkSpAIi51tgVv5pik6+01fyHlOBtIBozpjGXrs6eFA5G5GGhdIAgslFWQ9nOfoen6rJ0lrx1s/vCTQy0fbnvnwJTnS6YZrkpzWP4LjCXLg5SIexAJxGb7mwzTn535N8bOHosTAbA7zWsiFlwBj2/ZxKPqE3pvxx05RdaloiR6z9IFgZ7Q0GOGZhKqDkaJBk1cB0EOIto7kRuF4sp8oDKrDDxtggAoExidpqqs/18WDcweOUjnLDZ1bZZksTVJY9L7hoPRq/J0kmn6xOnozNtFYrq4VhBZqG8oAiNO+lN1nnkf5r7K5l83BYYsKlGj2qiUeVJ9ORjAolG1cGdN2/iCgsmsXfwFCY6ChcQhpzIpCExLyzSuXrfm48JYWJ3EEkaDsTh5RovuQ9mgX7dT6QDp3XC5ShAKmbF9+3rKcFl2GG36lQPLc4AaVUvbFW+pTtd5XmvDTt9hZywBQCYgHxEW84Wu45wsqfMQgWwZxj8HGr3PHj78XuSBxmkAqgBUob19HPSmzkDOMMsKg0W/MimXhOwZJ25SvKGl/qj9HDoDiAKL5pQY7l26Z4b/9bpHaDmW01/rf8ONWb4tGXmml5B0TollJoDpZJ3pppuGMaFqbw3Ehl1N0Ft2MFKtWnfB4uUVAOAGonUMSigNycArlav4DwxazJlGtbMdvNpnsevWo9/XMgAsGlYmRP2WwWJoDJqbG2HNjh4QZelYUnbJwCHcE52pu3qI1b3XIPg6I/M457pEoEcASNSJ4RR5AwTRyqWrJgyP0GXjg4K30b8YLJVORrJBqtl1dO++K2ZcPZpzsqVYIYABJpv+44baTkOuc7B9fPGV6cknA2XpoUZmzXSaNoIxHvcXiQXhZMtOtw6ziPJXupjGAFgsemRfNlEXDqjTE2vEAAbGAFEWOx2ZxufGnjJG3aGGM/r/6olfYfOR3SFuCP5eEIXA6cod8IdnNId2i2rfVhsArjNIR2rqWpuX590/4O0fXq+mz+oiVWCIAiCWqGSnHoMIK3YCgluKQDa9RQRB6Dz+bXzgFCov50BxJkGf5iUM6NOPpC5Gu7+Vm2PQFTrDaxhj0VS7RBrZO1pCC5BWzKyfEg69HvEZzfIHSYIQH5xpGi9sPRQbHeOaWYlGrxggYMbCjhzrrxkYT34CAL4O/6KOzTEx29dcEAsr5UCqeScYTPr3uGgNeE+GhhKRLuE1k+kY6/VFfsLDykbeoWyEm/ofD9+ohcIbO1t6HwcYI4ClOHjJwMWcIV0nvlJcQwCUCDH3SdWpqdyJPmsez5NknXC0kI9s3mk7de5BmmqRLVvrZL1YQ3F325cscI1nR0MdmefqarMbOwtdI8huLz7tTQ/T6cO9AqCdq68AAFUdTSTonB8wUe5FrHccjZRKq17zMn5vDedgu3A2hYkjvgLRwLQRYzIzWrsO7xcEwZ/amogQ7o0tmpUx1CrlFeJTg0iaOfCeKAq9yeSWAJBGVgisMrcsY1A0rI7rHx8k6aTO/BLnC7JeOsUSCsEAFg1pZfbBBcOGjh82n2ukQ4olEQRBzRpke2tGVgZ6eyO2BDWJUeMmKRKKlYR6o2NCvdExoWAk5Yl/FglGhxDxePrRt6SMNE0LH/sHhbuUGOyc4CQyUdKbGZg4oC8A4uisCh4Pv+c9eM7+4+YMxsJbJ4SNRrkTp4UQRBCCvRHjufoygalMOFuaF0k850ZcaRoBCnrbSW+rBteMWoZ9yfIFIOmVNiZZcz+CIEeTtcKkHJIUxitOWmamyZfVa7P4ZL0YHEgbY7GIUt7FT5XWB++CxdIOrr+8RtKJh1JkFK+/eHrnNh/3VWoqT+ufgshiN2441Lqv3Zxu3JTMaAgA10iwOqzz9+1qXow+DwIiAteZdJ/Wd2hHeHcGRcOKOlCgyQJHXOvjlcbTnr4SCCMGEAgaAC7phJZToRObtayM8wr2H6Ev52fUHz6kvOOcjLHYWFGvn3LOMbrfDKLlBY+gKJp8eoLEGCCK7JxfPWIgxvDlvpkkAMCKXYBQ0xRlOv2bAAP8p76NsF2oegZEkX2tZLJXg/oD0T7Okv8TOCOT6vBFRK6RFP+4PxDkGre2uyNX5pUdZB7PIbR6WnvMNuP7yfGSQUgspo2JhtQf95dIQICgyA79ex1HLFyS2bsD5MLADu9uuiXYHZuaaNxHldmif8fVkxe69egEGjq20IdkFpSy+2WjXKs36XbpzfJOvVnepTfJO8/+6HbqTbpdFrvxzewixw2rPmo/AeTRFyzInAFJUIir0S5QygWJOAfEGEpctu2Zg81bz9l/n8uPHQ6PTYnxAiT8Wgp/IbPddJYs6KtDSv5Q5QUqPLqPYdD1MCU0ipdWjMaJDQcEgLij+A3qdc9LqbP2F2MIBEluOrCxvt0lF+S2qVoiFx1YHwj3RhdkF2Y/mZc32FdxgKixQv++IAr3k8bTkovNVW4P9UTsiSoKA8AlWfBExEFbxRyNoty7RdJJPjWm9VU4A97Q2NM2GQkSi+YPT189YWcJpgnfxcjqeTWDdGkRIpj6GgFkshhe++jYu4/4A5vB6cxdxxiDxPRQ+CQwFsS3hs/DsY9jBLwE4MoB/J2t73ncU7yjUSAxy+GVu4MnNVXJBYglqqOIRdWhepN5umP499501bRQg79hwABOZxkGD57BMkX9hK6oOgwD7RXXGaUarSMjKI48J4lfGn1bpSoDwHjZC2PGBnBNx2NdN1YOHQOEzITckWtIlENgcXvTpzAAOAGaIf0tryb5m60jJ3NO9tQJKGF0NEUbnSadHNnVdRMa0vdjqm3IUZ1e2pcS1rDUTnEwMlr0W481tnpV7zpy2nO6jRbjlpSsamDzZCZrMew8ubWp6edHbqNrMypw/dip+0RZ9OA0yxYMhG68ftLVgx7651/CbnelpvVUXLyI7r13He6p/Kv0+L/c7/zRNXeajaNGsxb8+R9LlePM+zRnQaQnBudaA3cWpr8PUF+FlwgMBKmlzvPInGG64mtG/ZSVmS5DueUaTMQiDMutwO2zVzCXJSe7qa5zeTzIT5EBY3zijGFrJ40dBGHg4cDnxPk79CnNijcAduK4wvW254mJjLUf/rGqFpfDNplhzwdemLI2oF9Z+sox3JB2JJgx8XfHM26QOprDtxPFrdcAP0YA17iht0NbMGL4cdbhOYQXaneEzDbjuj4SkjWZ/jOPeMxp7Fx7cH897Tr5PjrfT49JIt6m/uHZaQ8AMKvdvKo0Kxqpx2684XkazYFN3jSHaW2SmqRli0W0IZ2e2B8ajtgd3y++i92Mm1klljKj6aeYPOoaof1gzdCGroY3qtYcbGo53HbE5O+4Zv68/8twoQ4tu4G9BZ+gW+laKUqipy/WTvARDSuln+1qW61myDNnTviOPLF0sTCp9CqhvHiB5I94x3Qec/89ElQmpsiCAJAkC6fSah0rb1z1w35H/4Vw/gB+gFNe3gDi4zKreFreW9BUE9o+WkOS7zJhzJWMjbryvxGvwXDENwPn5pxPBKPr6if/2t259VPdndGQclkiHmH9NyH7CBCi4djkb6fN1hGrwFWH0khwGD4URdYFoO+QsL8GRCSKQhcF51dnIgAAUGa+jykLRqyXJCGSOnC/noFESQwjKL99XPb1vf+o+RC4nZ4UJcGdcjQABrCgP7K4uym4I5RjXCbOumxRyZzK2bfMLr5F6Aq+3NMQ2hPyh5eAc1mLabntjaHHqMdpK0ZRYuRzZaXnrKmdgfr63XT1xsV1peV5fyQwNWkFk/RFQrFRtQfbPurspm29gv85f1roGR4UNjYc6twR7o1OHxj/AQxQsgfrfrvu2NqOUji+RC3JDNbvxc8KKfWXFS8Byydti5Fxxp1ciQxmIfcYXrdhC3OWrOTm3DXQ209BUyKCJWs/GdNebTIueP/pFw9rDXzm98KeyKNE8S/wnmGW4weTXJKYPTh6uzEc2hNdHTTj+vIf1O5r8O8JBSPzGDFiDKwvDiAGg1HeKezvanEjfsvs8OFO6CMNHqPV8EnAH65k8dpgsg8RCGarfpMlQ3UfPtbQN319fTXdPWzJicMO4/K2jtBvOZExxReyWFR1NR7xPjxA/gPKgPHPOOdmna7e4nB6uhMTnhX9VfGUEwhKSe/iVjTRn/DY8EfJ2Tz2CYfdMLXXF56X0i5ZYZBDPbEJhNgEFj8t4IydGYUTEaVlm97JlkLP1rl2Aw0AkySWJIhSzqEo4QVPhxNBOBECwXp6n/7df3qnymUAq9nqEYpL5yDd9TcQMXQcv4HVbX6FLHm/3hMrH1XTNfy7v13Ts3ZjTV6GOOxbT4Q6w89wTUvWBM6kJG5HGHFSuv1ZmseTiZqalejY8nbElK5fy0hIEMj6ewA8PS99bdnD/Rez/GhAUWY4Zs3Uv8sSjVPXjRGDqBf+rvXoYm428Irm2OhYmpDteNaYpntMYCzFUjGWkIMABoElnr7P+uhhZLEbNk3QmVrd7ho4jnoQi2mnR8+UCGTVbdum0fRt06m43RWPUNCX2RAA6A2yZnOlk+wwo6ZmJap3fRTwaOwWs123AQlNAZLnSWCIX2FIXMaC2F816BuTp2UZ35EU3Q8/XnNIqaqqggTA2+6PYoBJTBDBwYmfGfzH4ESEHLFEjNXXhwBoGicAdIbSVLuByq0E1rTFz4KmH/Diiikse/RTzGDZE42FK/eeHDZ+c/RbNx4M//Mrq9cGag5UN9+FeLUVOH8ERcSE+j0H9gbr65sAAO5skGyzvCfpxEYklT/hl0VZbAt06j587tG9fQO43TWoN+STLAurRUkMJNsinmtxnVE+5bDSe6t3TD7jht2cqjnYZd9Ep2jyrzLybXdIktSRUGYtIXliiWSQ+n4HB4EYY9GsIvuTdjH4z6vW/50a0IC3D04jA0VPSJLQzlKvKTAow0Y6N+1ffxxb12+Fz2/zyQbpACCkBLrELHbDJ7WKK3LSNz4peVQf3OSWsqXrBg3O/K0giuHEJucpfPbJMvFoIIFESQikOc2/TC+x/FNbe23PbqwCAAxK+wUa6k56DSb5EFJoBAGSWd62YYeDjh0rHSCn/BFOMuWPPirppA4GxvtlA5gzTLs6VnQMrEQm0egGVmwEZo130+BB9e3MevlH0Br/9vze+R+/v3/opqM1PTeFemKjibghLqd4fT71kC4l3UwEOYyyB9meqPUX7/N4/jOhBEHMW/zd7p5Wb7MSU+eRRgYCIEpCt7PIdF9v5onte443ACnfi87K9iC/pLzXrDN19HhDc4nHXaysE/1DJmTdvPfA6Bq3fxFC6DsT7cP+/ftxqqOIVF33wYkThvy3JCKsRLV8rpGdiECMJW87gTFAkkW/JcO42pZjvuuUd9BL8G6MvNP9DgDgTtgw9vLhvb024XiPLzSXOJmYKCg2h/GpKVMHPb32g3+hdViHwfJEDBcLdrilnjmqyjMYY2R3mteNn5v98wd3jAv9sWdpCoUnkJZdHhkfKdsYy9HWyDpB0GI8jzg3J7Kx+MYiBiYwkvVyY0ae4aVBJc47zGnKuy2xT2Mf7H2ubzS7sQcNXRPV4cW6z9QYrtA0zc4Exh35ttXls7L+9d2PZyqx2OIBMnJ7anB9RWnU7hx03OcOztI4tzKRqTaH5bnmAP7rQOep2OeKrZdVAjABNbqXIHd6/6OrtfdB9F87BHBmHJOqNABINkgHY2kFCz7c2d4O/LSv3aZNm+DdZxM+WrezOByKTuUgllVo2d4jof6Fv/27BjScQU9FxTK48svEPEktaTrROV0QRdWs8qoZxUUtvz9RS3v2PHhefpzOMpSVLcXIoeWCp6HL5DTSkIAvMqLb050di2liptPiZzqxtrjIVuMJNPmOB03a+vW1BDw3YJzy8idw1fyrWOvRjVld9Z7SsomFbZl2R32PTaeuWDErTisqUMWr8NB3/mCrOxUbmeGwhQydaUeLKtuiv1/5OzQ2Np5B35W4Cealg+HdP1kszfZYQmJgOFfUId2egFMQBG5zWjqNJumYTxVr1ZgjdHizTuvCz875LZDR9muwYNY/2Ruaakfml+T07G/MPDpz4R7l5ZdfPuudZxdcWPjDZcxfG86MdvWWlozI9rRHhZOvvLVBAV76Yhl8ZWUlyi2zHfV14tZYWB2KgXXh05F0tiSKYiAzx3ortw5659UPnqTTv0ngRBlu+e4SrHo9wnqQgzzdIgrmXXXeS9xlZUtRVnYtVq2qZiPAsPT2TNKe1/AYHvvc/DidZQgGH8Tsskz2yZ5N8OIDAm4AUIspU3LYIH0DNXc0Y8eRHecc4+67f4olf/0Rfp+7jt0mzKKVOY/gjU1vnNHuKTyFj0fJrF0eichn9ZSOF1D9D77DnY57IGE6Lr+sh+3ZfQTtCNIilGMbDrBJ46Zh6zGF9PpP4fM9dX4+UQbZ9BHGFq5m9Ww2jhz5z0SB8txwuVwYOrQS5p7LWMloN32w/TBqalYC+BJln0VYhNGjl44/HGtZpUS1IpypOInUI25qBFHoMduNDyLj6hfzBz+vPfGnx7/olJfwDcNZY5rzIQ95eK3ziTZX4e4PrAapSIlpJSAI6AvO4n+ZyFSjWbctPcdyV9YI6zvH2ndqK1c/euE5uISvHV+qXggAZXgAzlEzjEPzGy7rdseuUhVtpCyJVkXRevRG6aDdYXgvPTe9trMtasnMtXRn7Gj0KDUR/jAevpD0X8JFwJdWmjhuQynuZ0MLPcjPP2bQCqySrsmvDpvj0OprAreerOm4R41pDkFiMbPNsG7KhPTlm59b274aqy8M9ZdwUfCF3dNAfAYv/ojj3fVAsEGtD9fGTtSWq1Zd949PHmr/taZp2aIkBLjGM6Kh2MRTHeHSKwZf/r69xRg9iHNfLrqEbza+2oWQPlRjr28t2ttzMHyC5Gyt9d4DQJfutDys2uWpJROyl4iyeCoSVBfstwZmHxn64wsz7SVcFFwgpYnDlXaYWUN1RaqqZYiycLJXL//1s8aDzR53eJMlzfQeEYk9/tiY9IItF3TeS/h6cUEXz6DjsKfLQcYEjXPK5OHunPLyGWhu3mGIRWMuBjCdTuzOC9u/xOnrJXxTcEGVpjXaQFpuRq3OJO7SFO4Qe9mLeZHoL3LM416OBKJzBEnoNAZjG8RPdl1Smv/F+IqB8ED4/X60u4u4Mz3joI7TpFhUGx3sjlQqUXW4KAnd9hzjL7T2to83d79Pfvgv5NSX8DXigioNAAQCu3CiNa/zOzeMfy8WVGs5xymr3fB+6UTX8l0NsQ/2+l7XWkMnLvS0l/A14n8AXf4N7vVSS5YAAAAASUVORK5CYII=" alt="Logo">
            <label style="color: #343333">${reportObj.currentUser.accounts.map(c => c.name)[0]}</label>
          </div>
        </div>
      `;

      clone.insertBefore(header, clone.firstChild);

      const all = [clone, ...clone.querySelectorAll('*')];
      all.forEach(el => {
        Array.from(el.classList).forEach(cls => {
          if (cls.startsWith('dark:')) {
            const lightClass = cls.replace('dark:', 'light:');
            el.classList.remove(cls);
            el.classList.add(lightClass);
          }
          if (cls == 'border') {
            el.classList.remove(cls);
          }
        });
      });

      // Adicionar clone no DOM para renderizar corretamente
      const container = document.createElement('div');
      container.style.position = 'absolute';
      container.style.left = '-9999px';
      container.style.backgroundColor = 'white';
      container.appendChild(clone);
      document.body.appendChild(container);

      const originalCanvases = original.querySelectorAll('canvas');
      const clonedCanvases = clone.querySelectorAll('canvas');

      originalCanvases.forEach((origCanvas, i) => {
        const cloneCanvas = clonedCanvases[i];
        const context = cloneCanvas.getContext('2d');
        context.drawImage(origCanvas, 0, 0);
      });

      await new Promise(resolve => setTimeout(resolve, 100));

      const pdf = new JsPDF({
        unit: 'mm',
        format: 'a4',
      });

      // Configurações de página
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10; // margem em mm
      const contentWidth = pageWidth - margin * 2;
      const contentHeight = pageHeight - margin * 2;

      // Gerar imagens do conteúdo com html2canvas
      const scale = 2;
      const options = {
        scale: scale,
        width: clone.offsetWidth,
        height: clone.offsetHeight,
        windowWidth: clone.scrollWidth,
        windowHeight: clone.scrollHeight,
        useCORS: true,
      };

      const canvas = await html2canvas(clone, options);
      const imgData = canvas.toDataURL('image/png');

      // Calcular proporções para o PDF
      const imgWidth = contentWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // Verificar se o conteúdo cabe em uma página
      let heightLeft = imgHeight;
      let position = 0;

      // Adicionar primeira página
      pdf.addImage(
        imgData,
        'PNG',
        margin,
        margin,
        imgWidth,
        imgHeight,
        undefined,
        'FAST'
      );
      heightLeft -= contentHeight;

      // Adicionar páginas adicionais se necessário
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(
          imgData,
          'PNG',
          margin,
          position,
          imgWidth,
          imgHeight,
          undefined,
          'FAST'
        );
        heightLeft -= contentHeight;
      }

      pdf.save('relatorio.pdf');
      document.body.removeChild(container);
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  },
};

const mutations = {
  [types.default.SET_ACCOUNT_REPORTS](_state, { metric, data }) {
    _state.accountReport.data[metric] = data;
  },
  [types.default.SET_HEATMAP_DATA](_state, heatmapData) {
    _state.overview.accountConversationHeatmap = heatmapData;
  },
  [types.default.TOGGLE_ACCOUNT_REPORT_LOADING](_state, { metric, value }) {
    _state.accountReport.isFetching[metric] = value;
  },
  [types.default.SET_CONVERSATIONS_LIST](_state, { conversations, page }) {
    _state.conversationsList.conversations = conversations;
    _state.conversationsList.currentPage = page;
  },
  [types.default.TOGGLE_CONVERSATIONS_LIST_LOADING](_state, flag) {
    _state.overview.uiFlags.isFetchingConversationsList = flag;
  },
  [types.default.TOGGLE_HEATMAP_LOADING](_state, flag) {
    _state.overview.uiFlags.isFetchingAccountConversationsHeatmap = flag;
  },
  [types.default.SET_ACCOUNT_SUMMARY](_state, summaryData) {
    _state.accountSummary = summaryData;
  },
  [types.default.SET_BOT_SUMMARY](_state, summaryData) {
    _state.botSummary = summaryData;
  },
  [types.default.SET_ACCOUNT_CONVERSATION_METRIC](_state, metricData) {
    _state.overview.accountConversationMetric = metricData;
  },
  [types.default.TOGGLE_ACCOUNT_CONVERSATION_METRIC_LOADING](_state, flag) {
    _state.overview.uiFlags.isFetchingAccountConversationMetric = flag;
  },
  [types.default.SET_AGENT_CONVERSATION_METRIC](_state, metricData) {
    _state.overview.agentConversationMetric = metricData;
  },
  [types.default.TOGGLE_AGENT_CONVERSATION_METRIC_LOADING](_state, flag) {
    _state.overview.uiFlags.isFetchingAgentConversationMetric = flag;
  },
  [types.default.SET_REPORT_REF](_state, ref) {
    _state.reportRef = ref;
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
